import * as pathToRegexp from 'path-to-regexp'
import { Geografi, isGeografi, isParti, Locale, Parti, Valgtype } from '../../domain'
import { beholderDeFlertalletRoute } from './beholder-de-flertallet'
import { mandatfordelingEnkelRoute } from './mandatfordeling-enkel'
import { mandatfordelingDetaljertRoute } from './mandatfordeling-detaljert'
import { oppslutningStedRoute } from './oppslutning-sted'
import { oppslutningPartiRoute } from './oppslutning-parti'
import { oppslutningKartRoute } from './oppslutning-kart'
import { oppslutningHistoriskRoute } from './oppslutning-historisk'
import { tendensRoute } from './tendens'
import { idFrom } from '../../api'
import { useLocale } from '../../../lib/intl/IntlProvider'
import { useCustomContext } from '../../../lib/context'
import { widgetNotFoundRoute } from './widget-not-found'
import { vissteDuAtRoute } from './visste-du-at'
import { mandatkampRoute } from './mandatkamp'
import { pastanderRoute } from './pastander'
import { pastanderPartiRoute } from './pastander-parti'

export const widgetRoutes = [
  beholderDeFlertalletRoute,
  mandatfordelingEnkelRoute,
  mandatfordelingDetaljertRoute,
  oppslutningStedRoute,
  oppslutningPartiRoute,
  oppslutningKartRoute,
  oppslutningHistoriskRoute,
  tendensRoute,
  vissteDuAtRoute,
  mandatkampRoute,
  pastanderRoute,
  pastanderPartiRoute,

  /* Widget not found */
  widgetNotFoundRoute
]

export const urls = {
  beholderDeFlertallet: pathToRegexp.compile(beholderDeFlertalletRoute.path),
  mandatfordelingEnkel: pathToRegexp.compile(mandatfordelingEnkelRoute.path),
  mandatfordelingDetaljert: pathToRegexp.compile(mandatfordelingDetaljertRoute.path),
  oppslutningSted: pathToRegexp.compile(oppslutningStedRoute.path),
  oppslutningParti: pathToRegexp.compile(oppslutningPartiRoute.path),
  oppslutningKart: pathToRegexp.compile(oppslutningKartRoute.path),
  oppslutningHistorisk: pathToRegexp.compile(oppslutningHistoriskRoute.path),
  tendens: pathToRegexp.compile(tendensRoute.path),
  vissteDuAt: pathToRegexp.compile(vissteDuAtRoute.path),
  mandatkamp: pathToRegexp.compile(mandatkampRoute.path),
  pastander: pathToRegexp.compile(pastanderRoute.path),
  partipastander: pathToRegexp.compile(pastanderPartiRoute.path)
}

function toUrl(widgetHost, path) {
  return widgetHost + path
}

type VT = 'kommune' | 'fylke'

export function useWidgetUrls() {
  const locale = useLocale()
  const { widgetHost: host } = useCustomContext()
  return {
    mandatfordelingUrl(grunndata: Geografi, valgtype: VT, type: 'enkel' | 'detaljert') {
      const id = idFrom(grunndata)
      if (type === 'detaljert') {
        return toUrl(host, urls.mandatfordelingDetaljert({ id, locale, valgtype }))
      } else if (type === 'enkel') {
        return toUrl(host, urls.mandatfordelingEnkel({ id, locale, valgtype }))
      }
      throw new Error(`Kan ikke generere mandatfordelingsurl for ${grunndata.navn.nb}`)
    },
    oppslutningUrl(grunndata: Geografi | Parti, valgtype: VT, type: 'antall' | 'endring') {
      if (isGeografi(grunndata)) {
        const id = idFrom(grunndata)
        return toUrl(host, urls.oppslutningSted({ id, valgtype, type, locale }))
      } else if (isParti(grunndata)) {
        return toUrl(host, urls.oppslutningParti({ id: grunndata.id, valgtype, type, locale }))
      }
      throw new Error(`Kan ikke generere oppslutningsurl for ${grunndata}`)
    },
    beholderDeFlertalletUrl(grunndata: Geografi, valgtype: VT) {
      const id = idFrom(grunndata)
      return toUrl(host, urls.beholderDeFlertallet({ id, valgtype, locale }))
    },
    oppslutningKartUrl(parti: Parti, valgtype: VT, type: 'antall' | 'endring') {
      return toUrl(host, urls.oppslutningKart({ id: parti.id, valgtype, locale, type }))
    },
    historiskUrl(parti: Parti, valgtype: VT) {
      return toUrl(host, urls.oppslutningHistorisk({ id: parti.id, valgtype, locale }))
    },
    tendensUrl(valgtype: VT) {
      return toUrl(host, urls.tendens({ valgtype, locale }))
    },
    vissteduatUrl(grunndata: Geografi, valgtype: VT) {
      const id = idFrom(grunndata)
      return toUrl(host, urls.vissteDuAt({ id, valgtype, locale }))
    },
    mandatkampUrl(grunndata: Geografi, valgtype: VT) {
      const id = idFrom(grunndata)
      return toUrl(host, urls.mandatkamp({ id, valgtype, locale }))
    },
    pastanderUrl(grunndata: Geografi, valgtype: VT) {
      const id = idFrom(grunndata)
      return toUrl(host, urls.pastander({ id, valgtype, locale }))
    },
    partipastanderUrl(parti: Parti, valgtype: VT) {
      const id = parti.id
      return toUrl(host, urls.partipastander({ id, valgtype, locale }))
    }
  }
}
